import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate, Link } from "react-router-dom";
import { LayoutDashboard, Cake, IceCream, FileText, MessageSquare, Settings, LogOut, ShoppingBag, Package, FolderTree, Users, Grid3x3 } from "lucide-react";
import api from "../../lib/api";
import Logo from "../../components/Logo";

export default function AdminLayout() {
    const [user, setUser] = useState(null);
    const [checking, setChecking] = useState(true);
    const navigate = useNavigate();

    useEffect(() => {
        api.get("/auth/me")
            .then((r) => setUser(r.data))
            .catch(() => navigate("/admin/login"))
            .finally(() => setChecking(false));
    }, [navigate]);

    const logout = async () => {
        try { await api.post("/auth/logout"); } catch {}
        localStorage.removeItem("cc_admin_token");
        navigate("/admin/login");
    };

    if (checking) return <main className="min-h-screen flex items-center justify-center text-ink-soft">Loading…</main>;
    if (!user) return null;

    const navItems = [
        { to: "/admin", end: true, label: "Dashboard", icon: LayoutDashboard },
        { to: "/admin/categories", label: "Gallery Categories", icon: FolderTree },
        { to: "/admin/cakes", label: "Gallery Cakes", icon: Cake },
        { to: "/admin/shop-categories", label: "Shop Categories", icon: Grid3x3 },
        { to: "/admin/products", label: "Shop Products", icon: ShoppingBag },
        { to: "/admin/orders", label: "Orders", icon: Package },
        { to: "/admin/customers", label: "Customers", icon: Users },
        { to: "/admin/flavours", label: "Flavours", icon: IceCream },
        { to: "/admin/blogs", label: "Blogs", icon: FileText },
        { to: "/admin/inquiries", label: "Inquiries", icon: MessageSquare },
        { to: "/admin/settings", label: "Settings", icon: Settings },
    ];

    return (
        <div className="min-h-screen bg-[#FFF7FB] flex" data-testid="admin-layout">
            <aside className="hidden lg:flex w-64 bg-[#111111] text-white flex-col">
                <Link to="/" className="px-6 py-6 border-b border-white/10 inline-flex">
                    <Logo variant="light" className="h-10" />
                </Link>
                <nav className="flex-1 py-4">
                    {navItems.map((n) => (
                        <NavLink
                            key={n.to}
                            to={n.to}
                            end={n.end}
                            className={({ isActive }) =>
                                `flex items-center gap-3 px-6 py-3 text-sm transition-colors ${
                                    isActive ? "bg-white/5 text-blush border-l-2 border-blush" : "text-white/80 hover:bg-white/5"
                                }`
                            }
                            data-testid={`admin-nav-${n.label.toLowerCase()}`}
                        >
                            <n.icon size={16} /> {n.label}
                        </NavLink>
                    ))}
                </nav>
                <div className="px-6 py-4 border-t border-white/10">
                    <p className="text-xs text-white/60 mb-2">{user.email}</p>
                    <button onClick={logout} className="flex items-center gap-2 text-xs text-white/80 hover:text-blush" data-testid="admin-logout">
                        <LogOut size={14} /> Logout
                    </button>
                </div>
            </aside>

            <main className="flex-1 lg:ml-0 overflow-x-hidden">
                <header className="bg-white border-b border-[#EAE3DE] px-6 py-4 flex items-center justify-between">
                    <h1 className="font-display text-xl">CMS</h1>
                    <div className="flex items-center gap-4">
                        <Link to="/" className="text-xs tracking-[0.2em] uppercase text-ink-soft hover:text-blush">View Site →</Link>
                        <button onClick={logout} className="lg:hidden text-ink-soft hover:text-blush" data-testid="admin-logout-mobile">
                            <LogOut size={16} />
                        </button>
                    </div>
                </header>
                <div className="p-6 md:p-8">
                    <Outlet />
                </div>
            </main>
        </div>
    );
}
